/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from "@/shared/state";

import { v1 as uuid } from "uuid";

type FetchSettings = { pageSize: number; page?: number; filters?: Filter[] };
type Filter = { column: string; operator: FilterOperator; compareTo: string };
type FilterOperator = "gt" | "lt" | "gte" | "lte" | "e" | "ne";

export interface LeadOrigin {
  platform: string;
  source: string;
  external_user_id?: string;
  first_captured_at?: string;
  social_accounts?: Array<{ platform: string; external_user_id: string }>;
}

export interface LeadInteraction {
  type: string;
  text?: string;
  /** Set instead of `text` for a photo/video/audio/file DM (Meta never
   * includes message text for those -- only an attachments array). */
  attachment_type?: string;
  reaction_type?: string;
  property_id?: string;
  publication_id?: string;
  permalink?: string;
  captured_at: string;
  platform?: string;
  /** Client-only: true while an optimistically-added outbound reply is still in flight. */
  pending?: boolean;
  /** Client-only: true when an optimistically-added outbound reply failed to send. */
  failed?: boolean;
  /** Client-only: id used to match an optimistic entry to its start/success/error events. */
  localId?: string;
}

export interface ClientContact {
  type: string;
  value: string;
  label?: string;
  /** Real username/handle (e.g. Instagram), when known -- the value itself
   * may be an opaque platform-scoped id that can't build a working public
   * profile URL on its own. */
  handle?: string;
  /** Where a facebook/instagram contact was first captured from (message,
   * postback, comment, reaction, lead_ad...) -- a message/postback id is
   * page-scoped and never resolves to a public profile URL, so it links to
   * the DM conversation instead. */
  source?: string;
}

export interface ClientAddressFields {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface ClientRow {
  client_id: string;
  full_name: string;
  email: string;
  phone: string;
  status: string;
  sub_state?: string;
  type_of_client: string;
  last_contact_date: string;
  preferences: Record<string, unknown>;
  /**
   * How many listings this client owns. Present so a list row can say what
   * kind of client somebody is without a second request: owning a listing is
   * what separates a seller from a buyer, and the count already rides in the
   * list response.
   */
  owned_properties_count?: number;
  cpf?: string;
  cnpj?: string;
  document?: string;
  birth_date?: string;
  nationality?: string;
  profession?: string;
  marital_status?: string;
  rg?: string;
  issuing_agency?: string;
  trade_name?: string;
  corporate_name?: string;
  address?: string;
  address_fields?: ClientAddressFields;
  monthly_income?: number;
  income_proof?: string;
  notes?: string;
  registration_date?: string;
  lead_origin?: LeadOrigin;
  lead_interactions?: LeadInteraction[];
  contacts?: ClientContact[];
  /**
   * `origin.channel` — how the record itself was created (manual, site,
   * a portal webhook, a spreadsheet import...). Distinct from
   * `lead_origin`, which tracks marketing/chat-capture attribution and is
   * absent for a client nobody captured through a platform -- a manual
   * entry and an import look identical there, and only this field tells
   * them apart.
   */
  origin_channel?: string;
  /**
   * `origin.source_detail` — the words the spreadsheet actually said
   * (Indicação, Portal, Placa). Absent when the channel already is that
   * word, as with Site, and absent for a client nobody imported.
   */
  origin_detail?: string;
  /**
   * `assignment.primary_broker` — the e-mail of the agency member who
   * answers for this client. The record's own owner, unlike the broker
   * running whichever attendance happens to be open.
   */
  primary_broker?: string;
  /** `assignment.captured_by` — the e-mail of the member who brought the client in. */
  captured_by?: string;
  /**
   * `assignment.external_captor` — the name of someone outside the agency who
   * brought the client in. Never set together with `captured_by`.
   */
  external_captor?: string;
  /**
   * `assignment.external_broker` — a broker outside the agency who works the
   * client, by name. `primary_broker`, if set, is the member answering for it.
   */
  external_broker?: string;
  /**
   * The client's effective roles: `demand_profile.client_roles`, or the
   * legacy `client_role` read as a list (`both` is seeker and owner).
   */
  client_roles?: string[];
  /** `origin.campaign`. */
  origin_campaign?: string;
  /** `metadata.legacy_code` — the code in the previous system. */
  legacy_code?: string;
  /** `personal_info.gender` (female, male, other, not_informed). */
  gender?: string;
  /** `personal_info.education` (elementary, high_school, higher, postgraduate). */
  education?: string;
  /** `personal_info.job_title` — the position held ("Cargo"). */
  job_title?: string;
  /** `personal_info.employer`. */
  employer?: string;
  /** `personal_info.industry` — line of business ("Ramo"). */
  industry?: string;
}

export type ClientCrudContext = {
  fetchId: string;
  refreshFetch: () => void;

  fetchSettings: FetchSettings;
  setFetchSettings: (fetchSettings: FetchSettings) => void;
  rows: ClientRow[];
  setRows: (rows: ClientRow[] | ((prev: ClientRow[]) => ClientRow[])) => void;

  totalPages: number;
  setTotalPages: (n: number) => void;
  totalCount: number | null;
  setTotalCount: (n: number | null) => void;

  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  isTableVisible: boolean;
  setIsTableVisible: (value: boolean) => void;
  toggleIsTableExpanded: () => void;

  isFormVisible: boolean;
  setIsFormVisible: (value: boolean) => void;

  editingEntity: Record<string, unknown> | null;
  setEditingEntity: (entity: Record<string, unknown> | null) => void;

  tableStatistics: Record<string, unknown> | null;
  setStatistics: (statistics: Record<string, unknown> | null) => void;
};

export const {
  Consumer: ClientCrudConsumer,
  Context: ClientCrudContext,
  Provider: ClientCrudProvider,
  useStore: useClientCrudStore,
} = createLocalZustand<ClientCrudContext>((initializer) => (set) => ({
  fetchId: uuid(),
  refreshFetch: () => set(() => ({ fetchId: uuid() })),

  rows: [],
  setRows: (rows) => set((state) => ({ rows: typeof rows === 'function' ? rows(state.rows) : rows })),

  isLoading: true,
  setIsLoading: (isLoading) => set(() => ({ isLoading })),

  isTableVisible: true,
  setIsTableVisible: (value) => set(() => ({ isTableVisible: value })),
  toggleIsTableExpanded: () =>
    set((state) => ({ isTableVisible: !state.isTableVisible })),

  isFormVisible: false,
  setIsFormVisible: (value) => set(() => ({ isFormVisible: value })),

  fetchSettings: {
    pageSize: initializer?.fetchSettings?.pageSize ?? 10,
    page: initializer?.fetchSettings?.page ?? 1,
  },
  setFetchSettings: (fetchSettings) => set(() => ({ fetchSettings })),

  totalPages: 1,
  setTotalPages: (n) => set(() => ({ totalPages: n })),
  totalCount: null,
  setTotalCount: (n) => set(() => ({ totalCount: n })),

  editingEntity: null,
  setEditingEntity: (entity) => set(() => ({ editingEntity: entity })),

  tableStatistics: null,
  setStatistics: (statistics) => set(() => ({ tableStatistics: statistics })),
}));
